import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { statisticsManager, teamsManager } from '../services/api/managers';
import BackButton from '../components/BackButton';
import '../styles/index.css';
import { FaTimes } from 'react-icons/fa';
import Header from '../components/Header';

// Fixed to only Premier League (ID: 39)
const PREMIER_LEAGUE_ID = 39;

// Predefined seasons
const AVAILABLE_SEASONS = [2024, 2023, 2022, 2021, 2020, 2019];

const StatisticsPage = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Fixed league as Premier League
  const leagueId = PREMIER_LEAGUE_ID;
  
  // State for season and team selection
  const [season, setSeason] = useState(2024);
  const [teamId, setTeamId] = useState(33); // Default: Manchester United
  
  // State for teams list
  const [teams, setTeams] = useState([]);
  const [loadingTeams, setLoadingTeams] = useState(false);
  
  // Load Premier League teams when season changes
  useEffect(() => {
    const fetchTeams = async () => {
      try {
        setLoadingTeams(true);
        const response = await teamsManager.fetchTeams(leagueId, season);
        if (response.status === 'success' && response.data && response.data.response) {
          setTeams(response.data.response);
          // If current team isn't in this league, select first from the list
          if (!response.data.response.some(team => team.team.id === teamId) && response.data.response.length > 0) {
            setTeamId(response.data.response[0].team.id);
          }
        } else {
          setTeams([]);
        }
      } catch (error) {
        console.error('Error loading teams:', error);
        setTeams([]);
      } finally {
        setLoadingTeams(false);
      }
    };
    
    fetchTeams();
  }, [season]);
  
  // Load statistics when team or season changes
  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        setLoading(true);
        setError(null);
        
        if (!teamId || !leagueId || !season) {
          setError('Team or season not selected');
          setLoading(false);
          return;
        }
        
        console.log(`Loading statistics for team ${teamId} in Premier League, season ${season}`);
        const response = await statisticsManager.fetchStatistics(leagueId, season, teamId);
        
        if (response.status === 'success' && response.data) {
          setData(response.data);
        } else {
          setError(response.message || 'Error loading data');
          setData(null);
        }
      } catch (err) {
        console.error('Error loading statistics:', err);
        setError('Error loading data: ' + (err.message || 'Unknown error'));
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    if (teamId && season) {
      fetchStatistics();
    }
  }, [teamId, season]);

  return (
    <div className="data-page">
      <Header title="Statistics Page" />
      
      <h2 className="statistics-title">Team Statistics</h2>
      
      <div className="content-container">
        {/* Season and team selectors */}
        <div className="controls">
          <div className="select-container">
            <label htmlFor="season-select">Season:</label>
            <select
              id="season-select"
              value={season}
              onChange={(e) => setSeason(Number(e.target.value))}
              disabled={loading || loadingTeams || teams.length === 0}
            >
              {AVAILABLE_SEASONS.map(year => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>
          </div>
          
          <div className="select-container">
            <label htmlFor="team-select">Team:</label>
            <select
              id="team-select"
              value={teamId}
              onChange={(e) => setTeamId(Number(e.target.value))}
              disabled={loading || loadingTeams || teams.length === 0}
            >
              {loadingTeams ? (
                <option value="">Loading teams...</option>
              ) : teams.length === 0 ? (
                <option value="">No teams available</option>
              ) : (
                teams.map(item => (
                  <option key={item.team.id} value={item.team.id}>
                    {item.team.name}
                  </option>
                ))
              )}
            </select>
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Loading data...</div>
        ) : error ? (
          <div className="error">
            <div className="error-title">Error:</div>
            <div className="error-message">{error}</div>
            <div className="error-tip">
              Try selecting a different team or season.
            </div>
          </div>
        ) : !data || !data.response ? (
          <div className="empty-data">No data available for selected team or season</div>
        ) : (
          <>
            {data.response && data.response.team && (
              <div className="team-header">
                <img 
                  src={data.response.team.logo} 
                  alt={data.response.team.name} 
                  className="team-logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = 'https://via.placeholder.com/100?text=Team';
                  }}
                />
                <h2>{data.response.team.name}</h2>
                {data.response.league && (
                  <div className="league-season">
                    <img 
                      src={data.response.league.logo} 
                      alt={data.response.league.name} 
                      className="league-logo-small"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/30?text=League';
                      }}
                    />
                    <span>Premier League - Season {season}</span>
                  </div>
                )}
              </div>
            )}
            
            {data.response && data.response.fixtures && (
              <div className="stats-grid">
                <div className="stats-card">
                  <h3>Matches</h3>
                  <div className="stat-row">
                    <div className="stat-item">
                      <div className="stat-value">{data.response.fixtures.played?.total || 0}</div>
                      <div className="stat-label">Total</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{data.response.fixtures.wins?.total || 0}</div>
                      <div className="stat-label">Wins</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{data.response.fixtures.draws?.total || 0}</div>
                      <div className="stat-label">Draws</div>
                    </div>
                    <div className="stat-item">
                      <div className="stat-value">{data.response.fixtures.loses?.total || 0}</div>
                      <div className="stat-label">Losses</div>
                    </div>
                  </div>
                </div>
                
                {data.response.goals && (
                  <div className="stats-card">
                    <h3>Goals</h3>
                    <div className="stat-row">
                      <div className="stat-item">
                        <div className="stat-value">{data.response.goals.for?.total?.total || 0}</div>
                        <div className="stat-label">Scored</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">{data.response.goals.against?.total?.total || 0}</div>
                        <div className="stat-label">Conceded</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">
                          {typeof data.response.goals.for?.total?.total === 'number' && 
                           typeof data.response.fixtures.played?.total === 'number' && 
                           data.response.fixtures.played.total > 0
                            ? (data.response.goals.for.total.total / data.response.fixtures.played.total).toFixed(2)
                            : '0.00'}
                        </div>
                        <div className="stat-label">Avg. scored</div>
                      </div>
                      <div className="stat-item">
                        <div className="stat-value">
                          {typeof data.response.goals.against?.total?.total === 'number' && 
                           typeof data.response.fixtures.played?.total === 'number' && 
                           data.response.fixtures.played.total > 0
                            ? (data.response.goals.against.total.total / data.response.fixtures.played.total).toFixed(2)
                            : '0.00'}
                        </div>
                        <div className="stat-label">Avg. conceded</div>
                      </div>
                    </div>
                  </div>
                )}
                
                {data.response.fixtures && (
                  <div className="stats-card">
                    <h3>Results by venue</h3>
                    <div className="stat-row">
                      <div className="stat-column">
                        <h4>Home</h4>
                        <div className="stat-item">
                          <div className="stat-value">{data.response.fixtures.played?.home || 0}</div>
                          <div className="stat-label">Matches</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">{data.response.fixtures.wins?.home || 0}</div>
                          <div className="stat-label">Wins</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">{data.response.fixtures.draws?.home || 0}</div>
                          <div className="stat-label">Draws</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">{data.response.fixtures.loses?.home || 0}</div>
                          <div className="stat-label">Losses</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">
                            {typeof data.response.goals.for?.total?.home === 'number' ? data.response.goals.for.total.home : 0}
                          </div>
                          <div className="stat-label">Scored</div>
                        </div>
                      </div>
                      
                      <div className="stat-column">
                        <h4>Away</h4>
                        <div className="stat-item">
                          <div className="stat-value">{data.response.fixtures.played?.away || 0}</div>
                          <div className="stat-label">Matches</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">{data.response.fixtures.wins?.away || 0}</div>
                          <div className="stat-label">Wins</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">{data.response.fixtures.draws?.away || 0}</div>
                          <div className="stat-label">Draws</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">{data.response.fixtures.loses?.away || 0}</div>
                          <div className="stat-label">Losses</div>
                        </div>
                        <div className="stat-item">
                          <div className="stat-value">
                            {typeof data.response.goals.for?.total?.away === 'number' ? data.response.goals.for.total.away : 0}
                          </div>
                          <div className="stat-label">Scored</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {data.response.goals && data.response.goals.for && data.response.goals.for.minute && (
                  <div className="stats-card">
                    <h3>Goal distribution by match period</h3>
                    <div className="goals-timing">
                      <div className="timing-periods">
                        <div className="period-row">
                          <div className="period-label">0-15'</div>
                          <div className="period-value">
                            <span className="goals-scored">{data.response.goals.for.minute['0-15']?.total || 0}</span>
                            <span className="goals-separator">:</span>
                            <span className="goals-conceded">{data.response.goals.against.minute['0-15']?.total || 0}</span>
                          </div>
                          <div className="period-percentage">
                            {data.response.goals.for.minute['0-15']?.percentage || '0%'}
                          </div>
                        </div>
                        <div className="period-row">
                          <div className="period-label">16-30'</div>
                          <div className="period-value">
                            <span className="goals-scored">{data.response.goals.for.minute['16-30']?.total || 0}</span>
                            <span className="goals-separator">:</span>
                            <span className="goals-conceded">{data.response.goals.against.minute['16-30']?.total || 0}</span>
                          </div>
                          <div className="period-percentage">
                            {data.response.goals.for.minute['16-30']?.percentage || '0%'}
                          </div>
                        </div>
                        <div className="period-row">
                          <div className="period-label">31-45'</div>
                          <div className="period-value">
                            <span className="goals-scored">{data.response.goals.for.minute['31-45']?.total || 0}</span>
                            <span className="goals-separator">:</span>
                            <span className="goals-conceded">{data.response.goals.against.minute['31-45']?.total || 0}</span>
                          </div>
                          <div className="period-percentage">
                            {data.response.goals.for.minute['31-45']?.percentage || '0%'}
                          </div>
                        </div>
                        <div className="period-row">
                          <div className="period-label">46-60'</div>
                          <div className="period-value">
                            <span className="goals-scored">{data.response.goals.for.minute['46-60']?.total || 0}</span>
                            <span className="goals-separator">:</span>
                            <span className="goals-conceded">{data.response.goals.against.minute['46-60']?.total || 0}</span>
                          </div>
                          <div className="period-percentage">
                            {data.response.goals.for.minute['46-60']?.percentage || '0%'}
                          </div>
                        </div>
                        <div className="period-row">
                          <div className="period-label">61-75'</div>
                          <div className="period-value">
                            <span className="goals-scored">{data.response.goals.for.minute['61-75']?.total || 0}</span>
                            <span className="goals-separator">:</span>
                            <span className="goals-conceded">{data.response.goals.against.minute['61-75']?.total || 0}</span>
                          </div>
                          <div className="period-percentage">
                            {data.response.goals.for.minute['61-75']?.percentage || '0%'}
                          </div>
                        </div>
                        <div className="period-row">
                          <div className="period-label">76-90'</div>
                          <div className="period-value">
                            <span className="goals-scored">{data.response.goals.for.minute['76-90']?.total || 0}</span>
                            <span className="goals-separator">:</span>
                            <span className="goals-conceded">{data.response.goals.against.minute['76-90']?.total || 0}</span>
                          </div>
                          <div className="period-percentage">
                            {data.response.goals.for.minute['76-90']?.percentage || '0%'}
                          </div>
                        </div>
                        <div className="period-row">
                          <div className="period-label">91-105'</div>
                          <div className="period-value">
                            <span className="goals-scored">{data.response.goals.for.minute['91-105']?.total || 0}</span>
                            <span className="goals-separator">:</span>
                            <span className="goals-conceded">{data.response.goals.against.minute['91-105']?.total || 0}</span>
                          </div>
                          <div className="period-percentage">
                            {data.response.goals.for.minute['91-105']?.percentage || '0%'}
                          </div>
                        </div>
                        <div className="period-row">
                          <div className="period-label">106-120'</div>
                          <div className="period-value">
                            <span className="goals-scored">{data.response.goals.for.minute['106-120']?.total || 0}</span>
                            <span className="goals-separator">:</span>
                            <span className="goals-conceded">{data.response.goals.against.minute['106-120']?.total || 0}</span>
                          </div>
                          <div className="period-percentage">
                            {data.response.goals.for.minute['106-120']?.percentage || '0%'}
                          </div>
                        </div>
                      </div>
                      <div className="timing-legend">
                        <div><span className="goals-scored">Scored</span> : <span className="goals-conceded">Conceded</span></div>
                        <div>% - percentage of goals scored in this period</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {(!data.response || !data.response.fixtures) && (
              <div className="no-stats">
                <p>No statistics available for this team</p>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default StatisticsPage;